import {
  AuthError,
  type AuthProvider,
  type AuthTokens,
  type SessionBroker,
} from '../contract/auth.js';

/** The slice of the API client the auth flows need (keeps tests provider-free). */
export interface AuthSession {
  restore(): Promise<boolean>;
  setTokens(tokens: AuthTokens): void;
  signOut(): Promise<void>;
}

/**
 * DOM-free auth flow controller. Views render `state` and call the intent
 * methods; every transition is explicit. The refresh credential's entire
 * JS lifetime is inside signIn(): SRP result → broker.adopt → gone.
 */

export type AuthScreen = 'signin' | 'signup' | 'confirm' | 'forgot' | 'reset' | 'home';

export interface AuthState {
  readonly screen: AuthScreen;
  readonly busy: boolean;
  /** User-facing error for the current screen, if any. */
  readonly error: string | undefined;
  /** Non-error guidance ("We emailed you a code…"). */
  readonly notice: string | undefined;
  /** Email carried between screens (signup→confirm, forgot→reset). */
  readonly email: string;
}

const initial: AuthState = {
  screen: 'signin',
  busy: false,
  error: undefined,
  notice: undefined,
  email: '',
};

export class AuthController {
  private state: AuthState = initial;

  constructor(
    private readonly provider: AuthProvider,
    private readonly broker: SessionBroker,
    private readonly api: AuthSession,
    private readonly onChange: (state: AuthState) => void,
  ) {}

  get current(): AuthState {
    return this.state;
  }

  private set(patch: Partial<AuthState>): void {
    this.state = { ...this.state, ...patch };
    this.onChange(this.state);
  }

  goTo(screen: AuthScreen): void {
    this.set({ screen, error: undefined, notice: undefined, busy: false });
  }

  /** Boot: cookie-backed restore. Lands on home or signin. */
  async start(): Promise<void> {
    this.set({ busy: true });
    try {
      const restored = await this.api.restore();
      this.set({ screen: restored ? 'home' : 'signin', busy: false });
    } catch {
      // Broker unreachable: land on signin; the user can still try.
      this.set({ screen: 'signin', busy: false });
    }
  }

  async signIn(email: string, password: string): Promise<void> {
    this.set({ busy: true, error: undefined, notice: undefined, email });
    try {
      const result = await this.provider.signIn(email, password);
      // The one-shot handoff: cookie authority replaces the JS-held credential.
      await this.broker.adopt(result.refreshToken);
      this.api.setTokens(result.tokens);
      this.set({ screen: 'home', busy: false });
    } catch (err) {
      if (err instanceof AuthError && err.code === 'USER_NOT_CONFIRMED') {
        try {
          await this.provider.resendConfirmationCode(email);
        } catch {
          // The confirm screen's resend button remains available.
        }
        this.set({
          screen: 'confirm',
          busy: false,
          notice: 'Confirm your email first — we sent you a new code.',
        });
        return;
      }
      this.fail(err);
    }
  }

  async signUp(email: string, password: string, passwordRepeat: string): Promise<void> {
    if (password !== passwordRepeat) {
      this.set({ error: 'Passwords do not match.', notice: undefined });
      return;
    }
    this.set({ busy: true, error: undefined, notice: undefined, email });
    try {
      await this.provider.signUp(email, password);
      this.set({
        screen: 'confirm',
        busy: false,
        notice: `We emailed a confirmation code to ${email}.`,
      });
    } catch (err) {
      this.fail(err);
    }
  }

  async confirmSignUp(code: string): Promise<void> {
    this.set({ busy: true, error: undefined });
    try {
      await this.provider.confirmSignUp(this.state.email, code);
      this.set({
        screen: 'signin',
        busy: false,
        notice: 'Email confirmed. Sign in to continue.',
      });
    } catch (err) {
      this.fail(err);
    }
  }

  async resendCode(): Promise<void> {
    this.set({ busy: true, error: undefined });
    try {
      await this.provider.resendConfirmationCode(this.state.email);
      this.set({ busy: false, notice: 'We sent a new code.' });
    } catch (err) {
      this.fail(err);
    }
  }

  async forgotPassword(email: string): Promise<void> {
    this.set({ busy: true, error: undefined, notice: undefined, email });
    try {
      await this.provider.forgotPassword(email);
      this.set({
        screen: 'reset',
        busy: false,
        notice: `If an account exists for ${email}, a reset code is on its way.`,
      });
    } catch (err) {
      // Enumeration safety: recovery always proceeds to the reset screen.
      if (err instanceof AuthError && err.code === 'RATE_LIMITED') {
        this.fail(err);
        return;
      }
      this.set({
        screen: 'reset',
        busy: false,
        notice: `If an account exists for ${email}, a reset code is on its way.`,
      });
    }
  }

  async resetPassword(code: string, newPassword: string, repeat: string): Promise<void> {
    if (newPassword !== repeat) {
      this.set({ error: 'Passwords do not match.', notice: undefined });
      return;
    }
    this.set({ busy: true, error: undefined });
    try {
      await this.provider.confirmForgotPassword(this.state.email, code, newPassword);
      this.set({
        screen: 'signin',
        busy: false,
        notice: 'Password updated. Sign in with your new password.',
      });
    } catch (err) {
      this.fail(err);
    }
  }

  async signOut(): Promise<void> {
    this.set({ busy: true });
    await this.api.signOut();
    this.set({ ...initial, notice: 'Signed out.' });
  }

  /** Called by the API layer when a background refresh finds no session. */
  sessionLost(): void {
    this.set({ ...initial, notice: 'Your session ended. Sign in again.' });
  }

  private fail(err: unknown): void {
    const message = err instanceof AuthError ? err.message : 'Something went wrong. Try again.';
    this.set({ busy: false, error: message, notice: undefined });
  }
}
